import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toFixed(2);
const DEP = { pending: "Pending", received: "Received", refund_due: "Refund due", refunded: "Refunded", kept: "Kept (non-refundable)" };

const call = async (path, opts) => {
  const r = await fetch(`${API}/api${path}`, { headers: { "Content-Type": "application/json" }, ...opts });
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || "Something went wrong.");
  return d;
};

function Reschedule({ data, color, onDone }) {
  const a = data.appointment;
  const [date, setDate] = useState(a.date);
  const [slots, setSlots] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setSlots(null);
    call(`/public/tenant/${data.store.slug}/appointment-slots?service_id=${a.service_id}&date=${date}&provider_id=${a.provider_id}`)
      .then((d) => setSlots((d.providers[0]?.slots || []).filter((s) => !(date === a.date && s === a.start)))).catch(() => setSlots([]));
  }, [date, a, data.store.slug]);
  const pick = (start) => call(`/public/appointments/${a.token}/reschedule`, { method: "POST", body: JSON.stringify({ date, start }) }).then(onDone).catch((e) => setErr(e.message));
  return (
    <div style={{ marginTop: 16 }} data-testid="reschedule-panel">
      <h3 style={{ fontSize: 16 }}>Pick a new time with {a.provider_name}</h3>
      <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date"
        style={{ padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, marginBottom: 12, fontSize: 15 }} />
      {err && <div style={{ color: "#b00", fontSize: 14, marginBottom: 8 }} data-testid="reschedule-error">{err}</div>}
      {slots === null && <p style={{ color: "#888" }}>Loading times…</p>}
      {slots && slots.length === 0 && <p style={{ color: "#888" }} data-testid="reschedule-no-slots">No open times that day — try another date.</p>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {(slots || []).map((s) => <button key={s} onClick={() => pick(s)} data-testid={`reschedule-slot-${s}`}
          style={{ border: `1px solid ${color}`, color, background: "#fff", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontSize: 14 }}>{s}</button>)}
      </div>
    </div>
  );
}

export default function ManageAppointment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => call(`/public/appointments/${token}`).then(setData).catch((e) => setErr(e.message)), [token]);
  useEffect(() => { load(); }, [load]);

  if (err && !data) return <div style={{ maxWidth: 560, margin: "60px auto", fontFamily: "system-ui", padding: 18 }} data-testid="manage-not-found"><h1>Appointment not found</h1><p style={{ color: "#666" }}>This link may be incorrect.</p></div>;
  if (!data) return <div style={{ padding: 40, fontFamily: "system-ui", color: "#888" }}>Loading…</div>;

  const a = { ...data.appointment, token };
  const color = data.store.color;
  const btn = { background: color, color: "#fff", border: 0, borderRadius: 999, padding: "11px 20px", fontSize: 15, cursor: "pointer", fontWeight: 600, textDecoration: "none", display: "inline-block" };
  const ghost = { ...btn, background: "transparent", color, border: `1px solid ${color}` };
  const cancelled = a.status === "cancelled";
  const doCancel = () => call(`/public/appointments/${token}/cancel`, { method: "POST" }).then((d) => { setData(d); setMode(null); setMsg("Your appointment was cancelled."); }).catch((e) => setErr(e.message));

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "28px 18px", fontFamily: "system-ui,-apple-system,sans-serif", color: "#111" }} data-testid="manage-appointment">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          {data.store.logo && <img src={data.store.logo} alt="" width="40" height="40" style={{ borderRadius: 10, objectFit: "cover" }} />}
          <h1 style={{ fontSize: 22, margin: 0 }} data-testid="manage-store-name">{data.store.name}</h1>
        </div>
        {msg && <div style={{ background: "#ecfdf3", color: "#065f46", padding: "10px 12px", borderRadius: 10, marginBottom: 12, fontSize: 14 }} data-testid="manage-msg">{msg}</div>}
        {err && <div style={{ background: "#fdecec", color: "#b00", padding: "10px 12px", borderRadius: 10, marginBottom: 12, fontSize: 14 }} data-testid="manage-error">{err}</div>}
        <div style={{ border: "1px solid #eee", borderRadius: 14, padding: 18 }}>
          <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Ref {a.ref}</div>
          <div style={{ fontSize: 19, fontWeight: 700, marginTop: 4 }} data-testid="manage-service">{a.service_name}</div>
          <div style={{ color: "#444", marginTop: 4 }} data-testid="manage-when">with {a.provider_name} · {a.date} at {a.start}–{a.end}</div>
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: cancelled ? "#b00" : "#065f46", textTransform: "uppercase" }} data-testid="manage-status">{a.status}</div>
          {a.deposit > 0 && a.deposit_status !== "none" && (
            <div style={{ marginTop: 12, fontSize: 14 }} data-testid="manage-deposit">
              Deposit {money(a.deposit)} · <strong>{DEP[a.deposit_status] || a.deposit_status}</strong>
              {a.deposit_status === "pending" && data.deposit.link && !cancelled && <div style={{ marginTop: 8 }}><a href={data.deposit.link} target="_blank" rel="noreferrer" style={{ ...btn, background: "#0F766E" }} data-testid="manage-pay-deposit">Pay deposit</a></div>}
              <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>{data.deposit.policy}</div>
            </div>
          )}
        </div>

        {!cancelled && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
            <a href={data.calendar_url} target="_blank" rel="noreferrer" style={ghost} data-testid="manage-gcal">Google Calendar</a>
            <a href={data.ics_url} style={ghost} data-testid="manage-ics">Apple / Outlook</a>
          </div>
        )}

        {data.can_change ? (
          <div style={{ marginTop: 20, borderTop: "1px solid #eee", paddingTop: 16 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={btn} onClick={() => { setErr(""); setMode(mode === "reschedule" ? null : "reschedule"); }} data-testid="manage-reschedule-btn">Reschedule</button>
              <button style={{ ...ghost, color: "#b00", borderColor: "#b00" }} onClick={() => { setErr(""); setMode("cancel"); }} data-testid="manage-cancel-btn">Cancel appointment</button>
            </div>
            {mode === "cancel" && (
              <div style={{ marginTop: 14, background: "#fff7f7", border: "1px solid #f3caca", borderRadius: 12, padding: 14 }} data-testid="cancel-confirm">
                <p style={{ margin: "0 0 10px" }}>Cancel this appointment?{a.deposit_status === "received" ? " Your deposit will be refunded." : ""}</p>
                <button style={{ ...btn, background: "#b00" }} onClick={doCancel} data-testid="cancel-confirm-yes">Yes, cancel</button>
                <button style={{ ...ghost, marginLeft: 8 }} onClick={() => setMode(null)} data-testid="cancel-confirm-no">Keep it</button>
              </div>
            )}
            {mode === "reschedule" && <Reschedule data={{ ...data, appointment: a }} color={color} onDone={(d) => { setData(d); setMode(null); setMsg("Your appointment was rescheduled."); }} />}
          </div>
        ) : !cancelled && a.status !== "completed" && (
          <p style={{ marginTop: 20, color: "#666", fontSize: 14 }} data-testid="manage-locked">
            Online changes close {data.cancel_window_hours} hours before your appointment. To make a change, please contact {data.store.name}{data.store.phone ? ` at ${data.store.phone}` : data.store.email ? ` at ${data.store.email}` : ""}.
          </p>
        )}
        {cancelled && data.store.slug && <p style={{ marginTop: 20 }}><a href={`/s/${data.store.slug}/appointments`} style={btn} data-testid="manage-book-again">Book again</a></p>}
        <div style={{ marginTop: 40, color: "#767676", fontSize: 12, textAlign: "center" }}>Powered by {data.store.product || "MorrCart"}</div>
      </div>
    </div>
  );
}
